import { memo, type FC } from "react"
import Box from "@mui/material/Box"
import Skeleton from "@mui/material/Skeleton"

import {
  getRibbonFixedHeight,
  LANE_TITLE_HEIGHT,
  MARKER_BAND_HEIGHT,
  ROW_GAP,
  SCRUBBER_HEIGHT,
  SCRUBBER_LABEL_HEIGHT,
  SCRUBBER_TOP_GAP,
  SUMMARY_ROW_HEIGHT,
  TICK_HEIGHT,
  X_AXIS_HEIGHT,
  type RibbonBlockOptions,
} from "./helpers"

// Faint enough to read as background, not as another bar.
const FAINT = { opacity: 0.5 }

// A reserved row of the strip, with its stand-in centred in it.
const row = (height: number) => ({
  height,
  flexShrink: 0,
  display: "flex",
  alignItems: "center",
})
/**
 * A placeholder ribbon: a bar per lane and one for the scrubber, with the rows
 * around them stood in for. It appears before anything has answered, so it says
 * "a strip with a scrubber is coming" and commits to nothing past that.
 *
 * It takes the full height of the block it replaces, so the map above keeps its
 * size and leaflet is never asked to re-measure. Each placeholder sits where its
 * real counterpart will, so nothing moves on the swap, and the rows between them
 * are stood in for rather than left blank — two bars adrift in a tall box read
 * as a broken card. What's left at the bottom is the legend's row.
 */
const RibbonSkeleton: FC<RibbonBlockOptions> = (options) => (
  <Box
    data-testid="ampacity-layer-skeleton"
    sx={{
      height: getRibbonFixedHeight(options),
      display: "flex",
      flexDirection: "column",
    }}
  >
    {/* The totals readout, or the neutral strip's lane title. */}
    <Box
      sx={row(
        (options.showSummaryRow ? SUMMARY_ROW_HEIGHT : 0) +
          (options.showLaneTitle ? LANE_TITLE_HEIGHT : 0),
      )}
    >
      {(options.showSummaryRow || options.showLaneTitle) && (
        <Skeleton variant="text" width={70} height={10} sx={FAINT} />
      )}
    </Box>

    {Array.from({ length: options.lanes }, (_, lane) => (
      <Skeleton
        key={lane}
        variant="rounded"
        height={options.rowHeight}
        sx={{
          borderRadius: "3px",
          // The strip bands every lane, so every placeholder gets the room.
          mt: options.showMarkerBands ? `${MARKER_BAND_HEIGHT}px` : 0,
          mb: `${ROW_GAP}px`,
          flexShrink: 0,
        }}
      />
    ))}

    {options.showScrubber && (
      <>
        <Box sx={{ height: TICK_HEIGHT, flexShrink: 0 }} />
        {/* The axis labels under the lanes. */}
        <Box sx={row(X_AXIS_HEIGHT)}>
          <Skeleton variant="text" width={40} height={8} sx={FAINT} />
        </Box>

        <Box sx={{ height: SCRUBBER_TOP_GAP, flexShrink: 0 }} />
        {/* "Whole line — n spans" and the Fit all button. */}
        <Box sx={row(SCRUBBER_LABEL_HEIGHT)}>
          <Skeleton variant="text" width={120} height={10} sx={FAINT} />
        </Box>

        <Skeleton
          variant="rounded"
          height={SCRUBBER_HEIGHT}
          sx={{ borderRadius: "3px", flexShrink: 0 }}
        />
      </>
    )}
  </Box>
)

export default memo(RibbonSkeleton)
